import React from 'react';

import { useAppSelector } from '../../store';
import { pulsarIdentify, pulsarLogout } from '@/utils/analytics/pulsar';
import { resolvePulsarIdentityAction } from './resolvePulsarIdentityAction';

/**
 * Bridges the owner-hub authenticated identity into the Pulsar beacon (heimdall). Mounted once in
 * `BaseElement` (routes.tsx) alongside `AnalyticsConsent`, so it spans every route.
 *
 * The beacon mints and owns the anonymous ids; the HOST ids are ours to supply:
 *   - `user_id`    ← the authenticated owner (`authentication.user.user_id`)
 *   - `session_id` ← the owner-hub session handed over at sign-in (`session.sessionId`, a uuid);
 *                    this is the host's own session, distinct from the beacon's rolling anonymous
 *                    session. Absent on support-impersonation, so it is passed only when present.
 * Both are OPAQUE references (never the auth token) — see `utils/analytics/pulsar`.
 *
 * We `identify` whenever an authenticated id is present (and re-identify if the session rotates),
 * and `logout` on either sign-out edge:
 *   - a signed-in id we identified this run goes away (mid-session sign-out — `prevUserId`); or
 *   - the boot auth check RESOLVES to a signed-out state (`authSettledToGuest`) — so a previous
 *     user's persisted `_u`/`_s` on a shared/returning browser (their token expired while the tab
 *     was closed) does not keep attributing this visitor's guest events to them.
 *
 * We deliberately do NOT logout while auth is still UNRESOLVED (`in_progress`): on a reload the boot
 * check runs against the localStorage token, so the persisted `_u`/`_s` should keep attributing
 * until it settles, rather than being wiped and re-set. Once `in_progress` is false the auth flow
 * has RESOLVED — and a false value here can only mean "resolved", never "not started yet", because
 * `APPLICATION_INITIALIZE` is dispatched synchronously at boot (`main.tsx`) while `loadConfig` — which
 * gates this component's mount — is deferred (a network fetch, plus a 10ms `setTimeout` on the
 * preload path). So the guest-clear is LEVEL-triggered on `!in_progress && !userId`: it fires
 * whether or not this particular mount observed the `in_progress` transition, which is what makes it
 * robust to the config-vs-auth mount race (config resolving after auth, or a token-less boot that
 * resolves entirely in microtasks). Firing on the resolved-to-guest state is safe here (and, unlike
 * `store/index.ts`'s session teardown, correct to key on it) because `pulsarLogout` clears ONLY the
 * beacon's `_u`/`_s` cookies — never the Redux `session` slice — so it cannot wipe the sign-up
 * funnel's fresh `sessionId`. `clearedForGuest` keeps the clear one-shot until the next identify.
 *
 * Renders nothing.
 */
const PulsarIdentity = (): null => {
  const userId = useAppSelector((state) => state.authentication.user.user_id);
  const sessionId = useAppSelector((state) => state.session.sessionId);
  const authInProgress = useAppSelector((state) => state.authentication.in_progress);

  const prevUserId = React.useRef<string | null>(null);
  // The resolved-to-guest clear is one-shot per signed-out episode (reset on the next identify), so
  // a guest whose `sessionId` mints mid-funnel doesn't re-fire logout on every change.
  const clearedForGuest = React.useRef<boolean>(false);

  React.useEffect(() => {
    if (authInProgress) return; // auth still resolving — keep any persisted _u/_s until it settles

    // Auth has RESOLVED (see the header: a false in_progress at a config-gated mount means resolved,
    // not pre-check). A signed-out resolution clears a previous user's persisted _u/_s.
    const authSettledToGuest = !userId && !clearedForGuest.current;

    const action = resolvePulsarIdentityAction(prevUserId.current, userId, sessionId, authSettledToGuest);
    if (action.type === 'identify') {
      pulsarIdentify(action.identity);
      prevUserId.current = userId;
      clearedForGuest.current = false; // new signed-in episode — allow a future guest-clear
    } else if (action.type === 'reidentify') {
      // Switched to a DIFFERENT user with no sign-out between (support impersonation): clear the
      // previous user's _u/_s first so the new user can't inherit a stale session_id, then identify.
      pulsarLogout();
      pulsarIdentify(action.identity);
      prevUserId.current = userId;
      clearedForGuest.current = false;
    } else if (action.type === 'logout') {
      pulsarLogout();
      prevUserId.current = null;
      clearedForGuest.current = true; // don't re-fire the guest-clear until we identify again
    }
  }, [userId, sessionId, authInProgress]);

  return null;
};

export default PulsarIdentity;
